/**
 * KeyRatiosComparison - Displays calculated key financial ratios with year comparison
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrendingUp, TrendingDown, Minus, CheckCircle, XCircle } from 'lucide-react';
import type { SIEProcessedData, KeyRatio } from './types';
import { formatPercentagePoints } from './utils';

interface KeyRatiosComparisonProps {
  currentYear: SIEProcessedData;
  previousYear: SIEProcessedData;
}

export function KeyRatiosComparison({ currentYear, previousYear }: KeyRatiosComparisonProps) {
  const currentFiscalYear = currentYear.metadata.fiscalYears.find(fy => fy.index === 0);
  const previousFiscalYear = previousYear.metadata.fiscalYears.find(fy => fy.index === 0);

  const currentYearLabel = currentFiscalYear?.end?.slice(0, 4) || 'Innevarande';
  const previousYearLabel = previousFiscalYear?.end?.slice(0, 4) || 'Föregående';

  const keyRatios = useMemo(() => {
    const currIncome = currentYear.aggregated.incomeStatement;
    const prevIncome = previousYear.aggregated.incomeStatement;
    const currBalance = currentYear.aggregated.balanceSheet;
    const prevBalance = previousYear.aggregated.balanceSheet;

    const ratios: KeyRatio[] = [];

    // 1. Bruttomarginal (Net result / Revenue)
    const currGrossMargin = currIncome.totalRevenue > 0 
      ? (currIncome.netResult / currIncome.totalRevenue) * 100 
      : 0;
    const prevGrossMargin = prevIncome.totalRevenue > 0 
      ? (prevIncome.netResult / prevIncome.totalRevenue) * 100 
      : 0;
    ratios.push({
      name: 'Vinstmarginal',
      currentValue: currGrossMargin,
      previousValue: prevGrossMargin,
      change: currGrossMargin - prevGrossMargin,
      unit: '%',
      isGood: currGrossMargin >= prevGrossMargin,
    });

    // 2. Rörelsemarginal (approximation using expenses excluding financial)
    const currOperatingMargin = currIncome.totalRevenue > 0 
      ? ((currIncome.totalRevenue - currIncome.totalExpenses) / currIncome.totalRevenue) * 100 
      : 0;
    const prevOperatingMargin = prevIncome.totalRevenue > 0 
      ? ((prevIncome.totalRevenue - prevIncome.totalExpenses) / prevIncome.totalRevenue) * 100 
      : 0;
    ratios.push({
      name: 'Rörelsemarginal',
      currentValue: currOperatingMargin,
      previousValue: prevOperatingMargin,
      change: currOperatingMargin - prevOperatingMargin,
      unit: '%',
      isGood: currOperatingMargin >= prevOperatingMargin,
    });

    // 3. Soliditet (Equity / Total Assets)
    const currSolvency = currBalance.totalAssets > 0 
      ? (currBalance.totalEquity / currBalance.totalAssets) * 100 
      : 0;
    const prevSolvency = prevBalance.totalAssets > 0 
      ? (prevBalance.totalEquity / prevBalance.totalAssets) * 100 
      : 0;
    ratios.push({
      name: 'Soliditet',
      currentValue: currSolvency,
      previousValue: prevSolvency,
      change: currSolvency - prevSolvency,
      unit: '%',
      isGood: currSolvency >= prevSolvency,
    });

    // 4. Skuldsättningsgrad (Liabilities / Equity)
    const currDebtRatio = currBalance.totalEquity > 0 
      ? currBalance.totalLiabilities / currBalance.totalEquity 
      : 0;
    const prevDebtRatio = prevBalance.totalEquity > 0 
      ? prevBalance.totalLiabilities / prevBalance.totalEquity 
      : 0;
    ratios.push({
      name: 'Skuldsättningsgrad',
      currentValue: currDebtRatio,
      previousValue: prevDebtRatio,
      change: currDebtRatio - prevDebtRatio,
      unit: 'ratio',
      isGood: currDebtRatio <= prevDebtRatio,
    });

    // 5. Omsättningstillväxt
    const revenueGrowth = prevIncome.totalRevenue > 0 
      ? ((currIncome.totalRevenue - prevIncome.totalRevenue) / prevIncome.totalRevenue) * 100 
      : 0;
    ratios.push({
      name: 'Omsättningstillväxt',
      currentValue: revenueGrowth,
      previousValue: 0, // N/A for growth
      change: revenueGrowth,
      unit: '%',
      isGood: revenueGrowth > 0,
    });

    // 6. Kostnadsökning
    const expenseGrowth = prevIncome.totalExpenses > 0 
      ? ((currIncome.totalExpenses - prevIncome.totalExpenses) / prevIncome.totalExpenses) * 100 
      : 0;
    ratios.push({
      name: 'Kostnadsförändring',
      currentValue: expenseGrowth,
      previousValue: 0, // N/A for growth
      change: expenseGrowth,
      unit: '%',
      isGood: expenseGrowth <= revenueGrowth, // Good if expenses grow slower than revenue
    });

    // 7. Kapitalets omsättningshastighet (Revenue / Total Assets)
    const currAssetTurnover = currBalance.totalAssets > 0 
      ? currIncome.totalRevenue / currBalance.totalAssets 
      : 0;
    const prevAssetTurnover = prevBalance.totalAssets > 0 
      ? prevIncome.totalRevenue / prevBalance.totalAssets 
      : 0;
    ratios.push({
      name: 'Kapitalets omsättningshastighet',
      currentValue: currAssetTurnover,
      previousValue: prevAssetTurnover,
      change: currAssetTurnover - prevAssetTurnover,
      unit: 'ratio',
      isGood: currAssetTurnover >= prevAssetTurnover,
    });

    return ratios;
  }, [currentYear, previousYear]);

  const formatValue = (value: number, unit: KeyRatio['unit']) => {
    if (!isFinite(value)) return '-';
    switch (unit) {
      case '%':
        return `${value.toFixed(1)}%`;
      case 'ratio':
        return value.toFixed(2);
      case 'days':
        return `${Math.round(value)} dagar`;
      default:
        return value.toFixed(2);
    }
  };

  const formatChange = (change: number, unit: KeyRatio['unit']) => {
    if (!isFinite(change)) return '-';
    const sign = change > 0 ? '+' : '';
    switch (unit) {
      case '%':
        return `${sign}${change.toFixed(1)}pp`;
      case 'ratio':
        return `${sign}${change.toFixed(2)}`;
      case 'days':
        return `${sign}${Math.round(change)}`;
      default:
        return `${sign}${change.toFixed(2)}`;
    }
  };

  const getChangeIcon = (change: number, isGrowthMetric: boolean = false) => {
    if (Math.abs(change) < 0.1) return <Minus className="h-4 w-4 text-muted-foreground" />;
    if (change > 0) return <TrendingUp className="h-4 w-4" />;
    return <TrendingDown className="h-4 w-4" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Nyckeltal</CardTitle>
        <CardDescription>
          Jämförelse av beräknade nyckeltal mellan räkenskapsåren
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px]">Nyckeltal</TableHead>
              <TableHead className="text-right min-w-[100px]">{currentYearLabel}</TableHead>
              <TableHead className="text-right min-w-[100px]">{previousYearLabel}</TableHead>
              <TableHead className="text-right min-w-[100px]">Förändring</TableHead>
              <TableHead className="text-center min-w-[80px]">Trend</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keyRatios.map((ratio, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">{ratio.name}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatValue(ratio.currentValue, ratio.unit)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {ratio.name.includes('tillväxt') || ratio.name.includes('förändring') 
                    ? '-' 
                    : formatValue(ratio.previousValue, ratio.unit)
                  }
                </TableCell>
                <TableCell className={`text-right tabular-nums ${
                  ratio.isGood ? 'text-success' : ratio.isGood === false ? 'text-destructive' : ''
                }`}>
                  {formatChange(ratio.change, ratio.unit)}
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <span className={ratio.isGood ? 'text-success' : ratio.isGood === false ? 'text-destructive' : 'text-muted-foreground'}>
                      {getChangeIcon(ratio.change)}
                    </span>
                    {ratio.isGood !== null && (
                      ratio.isGood 
                        ? <CheckCircle className="h-4 w-4 text-success" />
                        : <XCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-4 text-xs text-muted-foreground">
          <p><strong>Förklaring:</strong></p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li><strong>Vinstmarginal</strong> - Resultat delat med omsättning</li>
            <li><strong>Rörelsemarginal</strong> - Rörelseresultat delat med omsättning</li>
            <li><strong>Soliditet</strong> - Eget kapital delat med totala tillgångar</li>
            <li><strong>Skuldsättningsgrad</strong> - Skulder delat med eget kapital</li>
            <li><strong>Kapitalets omsättningshastighet</strong> - Omsättning delat med totala tillgångar</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
